import { Injectable, OnDestroy } from '@angular/core';
import { FavoritesService } from '@products/services/favorites.service';
import { PersonalisationAPIService } from '@services';
import { SiteConfigService } from '@services/site-config.service';
import { FilterStateManagerService } from '@shared/services/filter-state-manager.service';
import { ConfigurationId } from '@types';
import { Logger } from '@utils/logger';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FundListingComponentConfig } from '../fund-listing-component.config';
import {
  BaseFilter,
  FundFilter,
  FundFilterService,
} from './fund-filter.service';

const logger = Logger.getLogger('ProductFilterStateManagerService');

export interface AppliedFilterDetails {
  facets: string;
  search_term: string;
}

@Injectable({
  providedIn: 'root',
})
export class ProductFilterStateManagerService
  extends FilterStateManagerService
  implements OnDestroy {
  private componentConfiguration: FundListingComponentConfig;
  private filtersCreated = false;
  private filtersPreCalculated: boolean;
  private isActiveListUpdatedByFirmFunds: boolean;
  appliedFilterData: AppliedFilterDetails = { facets: '', search_term: '' };
  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    fundFilterService: FundFilterService,
    private siteConfigService: SiteConfigService,
    private favoriteService: FavoritesService,
    private personalisationService: PersonalisationAPIService
  ) {
    super(fundFilterService);
    this.filtersPreCalculated =
      this.siteConfigService.product.ppss?.filtersOptions !== undefined;
    this.personalisationService
      .getIsActiveListUpdatedByFirm$()
      ?.pipe(takeUntil(this.unsubscribe$))
      ?.subscribe(
        (isUpdated: boolean) =>
          (this.isActiveListUpdatedByFirmFunds = isUpdated)
      );
  }

  mapFilterState(fundListingComponentConfig: FundListingComponentConfig) {
    this.componentConfiguration = fundListingComponentConfig;
    super.mapState();
    if (this.filtersPreCalculated) {
      this.createFilters();
    }
  }

  protected createFilters(): void {
    if (this.filtersCreated && !this.isActiveListUpdatedByFirmFunds) {
      return;
    }
    // check if filters will be updated based on the firm level fund data
    // then clear the state of the filters and create them dynamically from the data
    // showing all share class data for the firm level active list
    if (this.isActiveListUpdatedByFirmFunds) {
      // deleting the existing default filter which was set when we got the br config
      if (this.defaultSelectedFilters.size > 0) {
        this.defaultSelectedFilters.has(FundFilter.PRIMARY_SHARE_CLASS)
          ? this.defaultSelectedFilters.delete(FundFilter.PRIMARY_SHARE_CLASS)
          : this.defaultSelectedFilters.delete(
              FundFilter.FAV_FUND_WITH_PRIMARY_SHARECLASS
            );
      }
      this.clearFilterState();
    } else {
      // will be working for other users as expected
      this.favoriteService
        ?.hasFavorites$()
        ?.pipe(takeUntil(this.unsubscribe$))
        .subscribe((hasFavorites) => {
          if (!hasFavorites && !this.siteConfigService.isNjBest()) {
            // NGC-14509: Remove Primary Share Class Default Filter for NjBest
            // to display all funds by default
            this.defaultSelectedFilters.set(FundFilter.PRIMARY_SHARE_CLASS, [
              'true',
            ]);
          }
          // WDE-2068: When we have favorite funds enabled,
          // we will display all favorite fund irrespective of primary or non-primary shareclass
          // followed by primary share class funds
          else if (hasFavorites && !this.siteConfigService.isNjBest()) {
            this.defaultSelectedFilters.set(
              FundFilter.FAV_FUND_WITH_PRIMARY_SHARECLASS,
              ['true']
            );
          }
        });
    }

    const primaryFilters: FundFilter[] = this.siteConfigService.product?.ppss.primaryFilters[
      this.componentConfiguration.configurationName
    ]
      .map((key) => FundFilter[key])
      .filter((fundFilter) => fundFilter);

    const additionalFilters: FundFilter[] = this.siteConfigService.product?.ppss.additionalFilters[
      this.componentConfiguration.configurationName
    ]
      .map((key) => FundFilter[key])
      .filter((fundFilter) => fundFilter);

    if (this.filtersPreCalculated && !this.isActiveListUpdatedByFirmFunds) {
      try {
        // set this.filtersWithValues from siteConfig
        [...primaryFilters, ...additionalFilters].forEach((filter) => {
          this.filtersWithValues.set(filter, this.processFilterOptions(filter));
        });
      } catch (e) {
        // pre calced filters not up-to-date
        logger.error('need to regenerate pre calced filters in site-config', e);
        this.filtersPreCalculated = false;
        return;
      }
    } else {
      // set this.filtersWithValues from data
      this.formatProductDataByFilters([
        ...primaryFilters,
        ...additionalFilters,
      ]);
    }
    primaryFilters.forEach((filterListItem: BaseFilter) => {
      this.state.primaryFilters.push(this.createFilterType(filterListItem)); // creates filter from filter config and this.filtersWithValues
    });

    additionalFilters.forEach((filterListItem: BaseFilter) => {
      this.state.additionalFilters.push(this.createFilterType(filterListItem));
    });

    // TODO: pick config from right path i.e., decorated config.
    this.state.morningStarFilter = {
      label: FundFilter.MORNINGSTAR_RATING.key,
      type: FundFilter.MORNINGSTAR_RATING,
      selected: [],
    };
    this.state.searchKeyword = {
      label: FundFilter.SEARCH_KEYWORD.key,
      type: FundFilter.SEARCH_KEYWORD,
    };
    this.filtersCreated = true;
    if (this.filtersPreCalculated) {
      this.sendNext('preCalcFilters');
    }
  }

  /**
   * Handle any possible business logic(s) to be applied on filter dropdown options.
   * This method does some action(s) which might be a hacky fix from 'core' & not being handled by either 'BR' or "UI extension".
   * @param filter dropdown filter with info
   * @returns map of final options for target dropdown filter
   */
  private processFilterOptions(filter: FundFilter): Map<string, number> {
    // Fetch the filter options for target filter.
    let filterOptions: [string, number][] = Object.entries(
      this.siteConfigService.product.ppss.filtersOptions[
        this.componentConfiguration.configurationName
      ][filter.key]
    );

    // ********** Do any business logic(s) **********
    // WDE-5530: Suppress "ETF option" in "share class filter" dropdown for "mutual funds".
    // [hacky fix] Match 'etf' as in 'english' and 'fnb' as in 'french' for "en-ca" and "fr-ca" respectively.
    if (
      this.componentConfiguration.configurationName === ConfigurationId.MF &&
      filter.key === 'SHARE_CLASS_NAME'
    ) {
      filterOptions = filterOptions.filter(
        (filterOption) =>
          !filterOption[0]?.toLowerCase().includes('etf') &&
          !filterOption[0]?.toLowerCase().includes('fnb')
      );
    }

    // Return the final filter options as 'map'.
    return new Map(filterOptions);
  }

  public mapPreFilterState(
    fundListingComponentConfig: FundListingComponentConfig,
    selectedPreFilter: string
  ) {
    this.componentConfiguration = fundListingComponentConfig;
    this.selectedPreFilter = selectedPreFilter;
    this.mapState();
    if (this.filtersPreCalculated) {
      this.createFilters();
    }
  }

  /**
   * Filter by morning star rating.
   * @param value input text
   */
  filterByMorningstarRatingPPSS(value: string) {
    if (value.trim() === '') {
      this.state.morningStarFilter.selected = [];
    } else {
      this.state.morningStarFilter.selected = [value.trim()];
    }
    this.filterLunr();
  }

  /**
   * Filter by search handler.
   * @param value input text
   * @param withRequery optional boolean for requery logic
   */
  searchByKeywordPPSS(value: string) {
    this.state.searchKeyword.selected = [value];
    this.filterLunr();
  }

  /**
   * Store the applied filter details.
   * @param trackSelectorItem applied filter details
   */
  setAppliedFilterData(trackSelectorItem: AppliedFilterDetails) {
    this.appliedFilterData = {
      facets: trackSelectorItem.facets,
      search_term: trackSelectorItem.search_term,
    };
  }

  getAppliedFilterData(): AppliedFilterDetails {
    return this.appliedFilterData;
  }

  /**
   * Sclear the filter state
   */
  private clearFilterState() {
    this.state.primaryFilters = [];
    this.state.additionalFilters = [];
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
